import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import ModalService from "src/modals"
import apiClient from "src/apiClient"
import { selectUserId } from "src/store/user"
import { gameModeName, isSurvival } from "src/utils/gameModes"
import { mapLabel } from "src/utils/mapSizes"

import Modal from "src/components/modals/Modal"
import "./GameHistoryModal.css"

/**
 * The player's finished games, each row a door into /replay/:id.
 *
 * This shelf used to sit on the landing page, under the hall of fame, which gave the home screen
 * a height it could not have: the hero is pinned to the viewport, so a growing list meant a
 * scrollbar down the middle of the pitch and a title squeezed out of its own layout. A list that
 * grows without bound belongs behind a door, and the door is one line on an unchanged hero.
 *
 * Fetched here rather than in the store because nothing else reads it — a finished game is
 * deliberately not in the bootstrap payload (GameViewSet.get_queryset excludes it), and the
 * request is only worth making once somebody asks to see it.
 */
const GameHistoryModal = () => {
  const { t } = useTranslation()
  const userId = useSelector(selectUserId)

  const [games, setGames] = useState(null)
  useEffect(() => {
    let cancelled = false
    apiClient
      .get("/game/history/")
      .then((response) => !cancelled && setGames(response.data || []))
      .catch(() => !cancelled && setGames([]))
    return () => {
      cancelled = true
    }
  }, [])

  /* What the row says happened, as a key into `home.historyResult`.

     **The seat's own `outcome` comes first** (SV14). A classic game can be read off the winning
     side, and was; a survival game cannot — every person plays the same side, so `winner_team` is
     the people's team whether you outlasted the rest or fell on round four, and comparing the two
     called every survival game a victory. The server writes a per-player verdict for exactly this,
     and reading it also makes the badge right for a classic draw without a second rule. */
  const seatOf = (game) => game.players.find((player) => player.id === userId)
  const resultOf = (game) => {
    const outcome = seatOf(game)?.outcome
    if (outcome) return outcome === "win" ? "victory" : outcome === "draw" ? "draw" : "defeat"
    // No verdict on the row: a game finished before the column existed, or one this player only
    // watched. Say what happened instead of judging them — in survival that is a person's name.
    if (isSurvival(game.mode)) return game.winner_user ? "survivorWon" : "draw"
    if (!game.winner_team) return "draw"
    const seat = seatOf(game)
    if (!seat) return game.winner_team === "human" ? "humansWon" : "zombiesWon"
    return seat.team === game.winner_team ? "victory" : "defeat"
  }
  const resultLabel = (game) => {
    const key = resultOf(game)
    return key === "survivorWon"
      ? t("home.historyResult.survivorWon", { name: game.winner_user?.username })
      : t(`home.historyResult.${key}`)
  }
  /* Who this game was against. In a classic game that is the other side; in survival the other
     side is the waves, which are not people and are named in the mode rather than here — so it is
     the other people you were outlasting, and "solo" when there were none. */
  const opponentsOf = (game) => {
    const seat = seatOf(game)
    if (isSurvival(game.mode)) {
      const others = game.players.filter((player) => !player.is_bot && player.id !== userId)
      return others.map((player) => player.username).join(", ") || t("home.historyAlone")
    }
    const others = game.players.filter((player) => (seat ? player.team !== seat.team : player.id !== userId))
    return others.map((player) => player.username).join(", ") || "—"
  }

  return (
    <Modal width="640px">
      <h1>{t("home.history")}</h1>

      {games === null && <p className="history-note">{t("home.historyLoading")}</p>}
      {games !== null && games.length === 0 && <p className="history-note">{t("home.historyEmpty")}</p>}

      {games !== null && games.length > 0 && (
        <div className="history-rows">
          {games.map((game) => (
            <Link
              key={game.id}
              to={`/replay/${game.id}`}
              className="history-row"
              onClick={() => ModalService.hideAll()}
            >
              <span className={`history-result ${resultOf(game)}`}>{resultLabel(game)}</span>
              <span className="history-opponents">{t("home.historyVersus", { opponents: opponentsOf(game) })}</span>
              {/* The mode leads the meta line: "1v1" says it in three characters and survival
                  needs a word, which is what `gameModeName` is for — one helper rather than a
                  ternary at each of the places that print one. */}
              <span className="history-meta">
                {t("home.historyMeta", {
                  // The map size beside the mode (requirement 35, M12), where the row names one.
                  mode: game.map_size ? `${gameModeName(game.mode)} · ${mapLabel(game.map_size)}` : gameModeName(game.mode),
                  rounds: game.rounds,
                  date: String(game.finished_at || "").slice(0, 10),
                })}
              </span>
              <span className="history-watch">{t("home.historyWatch")}</span>
            </Link>
          ))}
        </div>
      )}
    </Modal>
  )
}

export default GameHistoryModal
